import React, { useMemo, useState, useContext, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import IosShareIcon from "@mui/icons-material/IosShare";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";

// =====================================================================
// Constants
// =====================================================================
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// =====================================================================
// Calendar helpers
// =====================================================================
function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({
      date: new Date(year, month - 1, prevMonthDays - i),
      outside: true,
    });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), outside: false });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  return cells;
}

function toYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromYMD(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function to12h(time24) {
  if (!time24) return { h12: "12", mm: "00", period: "AM" };
  const [rawH, rawM = "00"] = time24.split(":");
  let h = parseInt(rawH, 10);
  if (isNaN(h)) return { h12: "12", mm: "00", period: "AM" };
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { h12: String(h), mm: rawM.padStart(2, "0"), period };
}

function to24h(h12, mm, period) {
  let h = parseInt(h12, 10);
  if (isNaN(h)) h = 12;
  if (period === "AM") {
    if (h === 12) h = 0;
  } else {
    if (h !== 12) h += 12;
  }
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// =====================================================================
// TimeSelector – compact version
// =====================================================================
const TimeSelector = ({ time24, onChange, label }) => {
  const isUnset = !time24;
  const { h12, mm, period } = to12h(time24 || "");
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minutes = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0"),
  );
  const safeH = isUnset ? "12" : h12;
  const safeM = isUnset ? "00" : mm;
  const safeP = isUnset ? "AM" : period;
  const selectSx = {
    border: "1px solid #ccc",
    borderRadius: "3px",
    fontSize: "0.72rem",
    padding: "1px 3px",
    cursor: "pointer",
    outline: "none",
    background: "#f9f9f9",
    fontWeight: 600,
    height: 22,
  };
  const placeholderSx = { ...selectSx, color: "#aaa" };

  return (
    <Box sx={{ textAlign: "center", mb: 0.4 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.3,
          fontWeight: 600,
          color: "#555",
          fontSize: "0.65rem",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "3px",
        }}
      >
        <Box
          component="select"
          value={isUnset ? "" : h12}
          onChange={(e) => onChange(to24h(e.target.value, safeM, safeP))}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              HH
            </option>
          )}
          {hours.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.8rem",
            color: isUnset ? "#aaa" : "inherit",
            lineHeight: 1,
          }}
        >
          :
        </Typography>
        <Box
          component="select"
          value={isUnset ? "" : mm}
          onChange={(e) => onChange(to24h(safeH, e.target.value, safeP))}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              MM
            </option>
          )}
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Box>
        <Box
          component="select"
          value={safeP}
          onChange={(e) => onChange(to24h(safeH, safeM, e.target.value))}
          sx={isUnset ? placeholderSx : selectSx}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </Box>
      </Box>
    </Box>
  );
};

// =====================================================================
// SingleDateTimePicker – compact version
// =====================================================================
const SingleDateTimePicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
  label = "Date & Time",
  minDate = null,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const today = new Date();
  const [calYear, setCalYear] = useState(
    () => fromYMD(date)?.getFullYear() ?? today.getFullYear(),
  );
  const [calMonth, setCalMonth] = useState(
    () => fromYMD(date)?.getMonth() ?? today.getMonth(),
  );

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 340;
    const dropdownWidth = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= dropdownHeight
        ? rect.bottom + window.scrollY + 4
        : rect.top + window.scrollY - dropdownHeight - 4;
    const spaceRight = window.innerWidth - rect.left;
    const left =
      spaceRight >= dropdownWidth
        ? rect.left + window.scrollX
        : rect.right + window.scrollX - dropdownWidth;
    setDropdownPos({ top, left });
  };

  const handleOpen = () => {
    updatePosition();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  const selectedDateObj = fromYMD(date);

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else setCalMonth((m) => m + 1);
  };

  const handleDayClick = (d) => onDateChange(toYMD(d));
  const cells = buildCalendarDays(calYear, calMonth);

  // Day-month only. The grid already shows one selected day at a time and the
  // month sits in the header, so the year is noise in a narrow column.
  const fmtDate = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-");
    if (!y || !m || !d) return ymd;
    return `${d}-${m}`;
  };
  const fmtTime = (t) => {
    if (!t) return "Time?";
    const { h12, mm, period } = to12h(t);
    return `${h12}:${mm} ${period}`;
  };

  const displayText = date ? `${fmtDate(date)}  ${fmtTime(time)}` : label;

  const dropdownEl = open
    ? ReactDOM.createPortal(
        <Paper
          ref={dropdownRef}
          elevation={8}
          sx={{
            position: "absolute",
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 99999,
            p: 1.5,
            borderRadius: 2,
            width: 240,
          }}
        >
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 0.75,
              mb: 1,
            }}
          >
            <TimeSelector label={label} time24={time} onChange={onTimeChange} />
          </Box>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <IconButton size="small" onClick={prevMonth} sx={{ p: 0.3 }}>
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: "0.78rem" }}
              >
                {MONTH_NAMES[calMonth]} {calYear}
              </Typography>
              <IconButton size="small" onClick={nextMonth} sx={{ p: 0.3 }}>
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                mb: 0.3,
              }}
            >
              {DAY_HEADERS.map((d) => (
                <Typography
                  key={d}
                  variant="caption"
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#888",
                    fontSize: "0.62rem",
                  }}
                >
                  {d}
                </Typography>
              ))}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                gap: 0,
              }}
            >
              {cells.map((cell, idx) => {
                const { date: cellDate, outside } = cell;
                const isSelected = isSameDay(cellDate, selectedDateObj);
                const isDisabled =
                  outside || (minDate && toYMD(cellDate) < minDate);
                let bg = "transparent";
                let color = isDisabled ? "#ccc" : "#222";
                let fontWeight = 400;
                if (isSelected) {
                  bg = "#1976d2";
                  color = "#fff";
                  fontWeight = 700;
                }
                return (
                  <Box
                    key={idx}
                    onClick={() => !isDisabled && handleDayClick(cellDate)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 24,
                      bgcolor: bg,
                      borderRadius: "50%",
                      cursor: isDisabled ? "default" : "pointer",
                      "&:hover": !isDisabled
                        ? { bgcolor: isSelected ? "#1565c0" : "#bbdefb" }
                        : {},
                      transition: "background 0.1s",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight,
                        color,
                        lineHeight: 1,
                        userSelect: "none",
                      }}
                    >
                      {cellDate.getDate()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: "0.72rem", py: 0.3, minWidth: 0 }}
              onClick={() => {
                onDateChange("");
                onTimeChange("");
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ fontSize: "0.72rem", py: 0.3 }}
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </Box>
        </Paper>,
        document.body,
      )
    : null;

  return (
    <Box ref={triggerRef} sx={{ display: "block", width: "100%" }}>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.4,
          border: open ? "1.5px solid #1976d2" : "1px solid rgba(0,0,0,0.2)",
          borderRadius: "4px",
          px: 0.75,
          py: "3px",
          cursor: "pointer",
          bgcolor: "#fff",
          // Sized for "05-08  10:30 AM" - no year to fit any more.
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          "&:hover": { borderColor: "#333" },
        }}
      >
        <CalendarTodayIcon
          sx={{ fontSize: 11, color: open ? "#1976d2" : "#888", flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.8rem",
            color: date ? "#222" : "#999",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
          }}
        >
          {displayText}
        </Typography>
      </Box>
      {dropdownEl}
    </Box>
  );
};

// =====================================================================
// Helpers (unchanged logic)
// =====================================================================
const getDaysInMonth = (monthYearStr) => {
  const [monthStr, yearStr] = monthYearStr.split(" ");
  const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
  const year = parseInt(yearStr);
  return new Date(year, monthIndex + 1, 0).getDate();
};

const formatTime24 = (time) => {
  if (!time) return "";
  return time.substring(0, 5);
};

// =====================================================================
// Main Component (compact UI + animated expandable Trip Details)
// =====================================================================
export default function CustomerFeedbackDashboard({
  selectedMonth,
  selectedDate,
  setSelectedDate,
  activeFilter,
  vehiclesData,
  onRefresh,
  onNavigateToBooking,
  accountIds,
  setSelectedMonth,
}) {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  const loggedUser = JSON.parse(localStorage.getItem("CapacitorStorage.user"));
  const user_id = loggedUser?.id;

  const [editingRows, setEditingRows] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Track expanded state for Trip Details per row
  const [expandedRows, setExpandedRows] = useState({});

  const toggleTripExpand = (bookingId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  };

  const extendedDays = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);

    const days = [];

    // 5 days from previous month
    const prevMonthLastDay = new Date(year, monthIndex, 0).getDate();
    for (let i = 4; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const prevMonth = monthIndex === 0 ? 11 : monthIndex - 1;
      const prevYear = monthIndex === 0 ? year - 1 : year;
      const monthShort = new Date(prevYear, prevMonth, 1).toLocaleString(
        "default",
        { month: "short" },
      );
      days.push({
        day,
        label: `${monthShort} ${prevYear}`,
        isCurrentMonth: false,
        isPrev: true,
      });
    }

    // Current month days
    const numDays = new Date(year, monthIndex + 1, 0).getDate();
    const monthShort = new Date(year, monthIndex, 1).toLocaleString("default", {
      month: "short",
    });
    for (let d = 1; d <= numDays; d++) {
      days.push({
        day: d,
        label: `${monthShort} ${year}`,
        isCurrentMonth: true,
        isPrev: false,
      });
    }

    // 5 days from next month
    const nextMonth = monthIndex === 11 ? 0 : monthIndex + 1;
    const nextYear = monthIndex === 11 ? year + 1 : year;
    const nextMonthShort = new Date(nextYear, nextMonth, 1).toLocaleString(
      "default",
      { month: "short" },
    );
    for (let d = 1; d <= 5; d++) {
      days.push({
        day: d,
        label: `${nextMonthShort} ${nextYear}`,
        isCurrentMonth: false,
        isPrev: false,
      });
    }

    return days;
  }, [selectedMonth]);

  const formatTime = (time) => {
    if (!time) return "N/A";
    const [hours, minutes] = time.split(":");
    const hr = parseInt(hours);
    const ampm = hr >= 12 ? "PM" : "AM";
    const displayHr = hr % 12 || 12;
    return `${displayHr}:${minutes} ${ampm}`;
  };

  const processedBookings = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);

    const targetDateStr = `${year}-${String(monthIndex + 1).padStart(
      2,
      "0",
    )}-${String(selectedDate).padStart(2, "0")}`;

    const result = [];

    vehiclesData.forEach((vehicle) => {
      const bookingsForDate = vehicle.bookings.filter((booking) => {
        const pickupDate = booking.trip_info?.pickup_date;
        const dropDate = booking.trip_info?.drop_date;

        if (!pickupDate) return false;

        if (!dropDate || dropDate === null || dropDate === "") {
          return pickupDate === targetDateStr;
        }

        return targetDateStr >= pickupDate && targetDateStr <= dropDate;
      });

      bookingsForDate.forEach((booking) => {
        result.push({
          vehicle_id: vehicle.vehicle_id,
          vehicle_name: vehicle.vehicle_name,
          vehicle_number: vehicle.vehicle_number,
          vehicle_code: vehicle.vehicle_code,
          booking: booking,
        });
      });
    });

    return result;
  }, [vehiclesData, selectedDate, selectedMonth]);

  const filteredBookings = useMemo(() => {
    if (activeFilter === "PENDING") {
      return processedBookings.filter(
        (row) => !row.booking.trip_info?.customer_feedback,
      );
    }
    return processedBookings;
  }, [processedBookings, activeFilter]);

  const handleFieldChange = (bookingId, field, value) => {
    setEditingRows((prev) => ({
      ...prev,
      [bookingId]: {
        ...prev[bookingId],
        [field]: value,
        hasChanges: true,
      },
    }));
  };

  const handleSaveRow = async (bookingId) => {
    const editedData = editingRows[bookingId];
    if (!editedData || !editedData.hasChanges) return;

    const bookingRow = filteredBookings.find(
      (row) => row.booking.booking_id === bookingId,
    );
    if (!bookingRow) return;

    const booking = bookingRow.booking;

    setSavingRows((prev) => ({ ...prev, [bookingId]: true }));

    try {
      const payload = {
        store_id,
        account_ids: accountIds,
        created_by: user_id,
        customer_id: booking.customer_id,
        booking_type: booking.type.includes("B") ? "B" : "E",
        customer_name: booking.customer_name,
        customer_mobile: booking.customer_mobile,
        trip_details: booking.trip_details,
        pickup_date: editedData.pickup_date ?? booking.trip_info?.pickup_date,
        pickup_time: editedData.pickup_time ?? booking.trip_info?.pickup_time,
        pax: booking.trip_info?.pax,
        pickup_location: booking.trip_info?.pickup_location,
        pickup_address: booking.trip_info?.pickup_address,
        drop_location: booking.trip_info?.drop_location,
        drop_address: booking.trip_info?.drop_address,
        drop_date: editedData.drop_date ?? booking.trip_info?.drop_date,
        drop_time: editedData.drop_time ?? booking.trip_info?.drop_time,
        trip_mode: booking.trip_info?.mode,
        quote: parseFloat(booking.amount) || 0,
        advance: parseFloat(booking.advance) || 0,
        vehicle_id: booking.vehicle_id,
        driver_id: booking.driver_id,
        driver_phone: booking.driver_phone,
        driver_voucher_no: booking.driver_voucher_no,
        confirmation: booking.confirmation,
        payment_details: booking.trip_info?.payment_details,
        instructions: booking.instructions,
        vehicle_type: booking.trip_info?.vehicle_type,
        isVehicleOther: false,
        isDriverOther: false,
        stage: booking.stage || "active",
        customer_feedback:
          editedData.customer_feedback ??
          booking.trip_info?.customer_feedback ??
          "",
      };

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/booking/${bookingId}`;
      const response = await axios.put(url, payload);

      if (response.data.success) {
        setSnackbar({
          open: true,
          message:
            response.data.statusMessage ||
            "Customer feedback updated successfully!",
          severity: "success",
        });
        // Pull the saved row back from the server BEFORE dropping the local
        // draft. This component unmounts on a tab switch, taking editingRows
        // with it, so without a refetch the saved value appears to vanish
        // until a full page reload.
        await onRefresh?.();
        setEditingRows((prev) => {
          const next = { ...prev };
          delete next[bookingId];
          return next;
        });
      } else {
        setSnackbar({
          open: true,
          message:
            response.data.statusMessage || "Failed to update customer feedback",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.statusMessage ||
          error?.response?.data?.message ||
          "Error updating customer feedback",
        severity: "error",
      });
    } finally {
      setSavingRows((prev) => {
        const newState = { ...prev };
        delete newState[bookingId];
        return newState;
      });
    }
  };

  const handleExcelExport = () => {
    if (filteredBookings.length === 0) {
      setSnackbar({
        open: true,
        message: "No trips to export",
        severity: "error",
      });
      return;
    }

    const excelData = filteredBookings.map((item, index) => ({
      "Sr. No": index + 1,
      Vehicle: item.vehicle_code,
      Driver: item.booking.driver_code || "N/A",
      "Start Time": formatTime(item.booking.trip_info?.pickup_time),
      "End Time": formatTime(item.booking.trip_info?.drop_time),
      "Vehicle Type": item.booking.trip_info?.vehicle_type || "N/A",
      "Customer Name": item.booking.customer_name,
      "Customer Mobile": item.booking.customer_mobile,
      "Trip Details": item.booking.trip_details || "N/A",
      "Customer Feedback": item.booking.trip_info?.customer_feedback || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const columnWidths = [
      { wch: 8 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 30 },
      { wch: 40 },
    ];
    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Feedback");

    const [monthStr, yearStr] = selectedMonth.split(" ");
    const filename = `Customer_Feedback_${selectedDate}_${monthStr}_${yearStr}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  const getEditedValue = (bookingId, field, defaultValue) => {
    return editingRows[bookingId]?.[field] ?? defaultValue;
  };

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* ── Header / Day strip (compact) ── */}
      <Box
        sx={{
          px: 1.5,
          pt: 0.75,
          pb: 0.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.6,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            {selectedDate} {selectedMonth} • {filteredBookings.length} trips
          </Typography>

          {/* Export button compact */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#3873f3",
              px: 1,
              py: 0.4,
              borderRadius: 1.5,
              cursor: "pointer",
            }}
            onClick={handleExcelExport}
          >
            <IosShareIcon sx={{ fontSize: 16, color: "#fff" }} />
          </Box>
        </Box>

        {/* Day strip – compact 26px chips */}
        {/* Day strip — chips reduced from 34px → 26px */}
        <Box sx={{ overflowX: "auto", pb: 0.4 }}>
          <Box sx={{ display: "flex", gap: 0.35, minWidth: "max-content" }}>
            {/* ── LEFT: previous‑month button ── */}
            {extendedDays.length > 0 && extendedDays[0].isPrev && (
              <Box
                onClick={() => {
                  const prevItem = extendedDays[0];
                  setSelectedMonth(prevItem.label);
                  setSelectedDate(1);
                }}
                sx={{
                  minWidth: 26,
                  height: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 0.75,
                  cursor: "pointer",
                  fontSize: "0.72rem",
                  fontWeight: 400,
                  bgcolor: "grey.50",
                  color: "text.disabled",
                  border: "1px dashed #ccc",
                  px: 1,
                  "&:hover": {
                    bgcolor: "grey.200",
                    color: "text.primary",
                  },
                }}
              >
                {extendedDays[0].label.split(" ")[0]}
              </Box>
            )}

            {/* ── CURRENT MONTH DAYS ── */}
            {extendedDays
              .filter((item) => item.isCurrentMonth)
              .map((item, idx) => {
                const isSelected = selectedDate === item.day;
                return (
                  <Box
                    key={idx}
                    onClick={() => setSelectedDate(item.day)}
                    sx={{
                      minWidth: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 0.75,
                      cursor: "pointer",
                      fontSize: "0.72rem",
                      fontWeight: isSelected ? 600 : 400,
                      bgcolor: isSelected ? "primary.main" : "grey.100",
                      color: isSelected ? "white" : "text.primary",
                      border: "none",
                      "&:hover": {
                        bgcolor: isSelected ? "primary.dark" : "grey.200",
                        color: isSelected ? "white" : "text.primary",
                      },
                    }}
                  >
                    {item.day}
                  </Box>
                );
              })}

            {/* ── RIGHT: next‑month button ── */}
            {extendedDays.length > 0 &&
              extendedDays[extendedDays.length - 1].isPrev === false &&
              !extendedDays[extendedDays.length - 1].isCurrentMonth && (
                <Box
                  onClick={() => {
                    const nextItem = extendedDays[extendedDays.length - 1];
                    setSelectedMonth(nextItem.label);
                    setSelectedDate(1);
                  }}
                  sx={{
                    minWidth: 26,
                    height: 26,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 0.75,
                    cursor: "pointer",
                    fontSize: "0.72rem",
                    fontWeight: 400,
                    bgcolor: "grey.50",
                    color: "text.disabled",
                    border: "1px dashed #ccc",
                    px: 1,
                    "&:hover": {
                      bgcolor: "grey.200",
                      color: "text.primary",
                    },
                  }}
                >
                  {extendedDays[extendedDays.length - 1].label.split(" ")[0]}
                </Box>
              )}
          </Box>
        </Box>
      </Box>

      {/* ── Table (compact, animated expandable Trip Details) ── */}
      <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
        <Table size="small" sx={{ tableLayout: "fixed" }} stickyHeader>
          <TableHead>
            <TableRow>
              {[
                ["Vehicle", "46px"],
                ["Driver", "46px"],
                ["Start Date & Time", "92px"],
                ["End Date & Time", "92px"],
                ["Veh Type", "58px"],
                ["Customer", "120px"],
                ["Trip Details", "200px"],
                ["Customer Feedback", "200px"],
                ["Action", "46px"],
              ].map(([label, width]) => (
                <TableCell
                  key={label}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    py: 0.5,
                    px: 0.75,
                    width,
                    lineHeight: 1.2,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredBookings.map((row) => {
              const bookingId = row.booking.booking_id;
              const hasChanges = editingRows[bookingId]?.hasChanges;
              const isSaving = savingRows[bookingId];
              const cellSx = { py: 0.4, px: 0.75, fontSize: "0.85rem" };

              const tripDetails = row.booking.trip_details || "";
              const isExpanded = expandedRows[bookingId] || false;

              // Determine if the text is long enough to need a toggle
              const showToggle = tripDetails.length > 50;

              return (
                <TableRow
                  key={bookingId}
                  hover
                  sx={{
                    backgroundColor: hasChanges
                      ? "rgba(56,115,243,0.05)"
                      : "inherit",
                  }}
                >
                  {/* Vehicle */}
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      {row.vehicle_code}
                    </Typography>
                  </TableCell>

                  {/* Driver */}
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      {row.booking.driver_code || "-"}
                    </Typography>
                  </TableCell>

                  {/* Start Date & Time */}
                  <TableCell sx={cellSx}>
                    <SingleDateTimePicker
                      label="Start"
                      date={getEditedValue(
                        bookingId,
                        "pickup_date",
                        row.booking.trip_info?.pickup_date || "",
                      )}
                      time={getEditedValue(
                        bookingId,
                        "pickup_time",
                        formatTime24(row.booking.trip_info?.pickup_time),
                      )}
                      onDateChange={(val) =>
                        handleFieldChange(bookingId, "pickup_date", val)
                      }
                      onTimeChange={(val) =>
                        handleFieldChange(bookingId, "pickup_time", val)
                      }
                    />
                  </TableCell>

                  {/* End Date & Time */}
                  <TableCell sx={cellSx}>
                    <SingleDateTimePicker
                      label="End"
                      date={getEditedValue(
                        bookingId,
                        "drop_date",
                        row.booking.trip_info?.drop_date || "",
                      )}
                      time={getEditedValue(
                        bookingId,
                        "drop_time",
                        formatTime24(row.booking.trip_info?.drop_time),
                      )}
                      onDateChange={(val) =>
                        handleFieldChange(bookingId, "drop_date", val)
                      }
                      onTimeChange={(val) =>
                        handleFieldChange(bookingId, "drop_time", val)
                      }
                      minDate={getEditedValue(
                        bookingId,
                        "pickup_date",
                        row.booking.trip_info?.pickup_date || null,
                      )}
                    />
                  </TableCell>

                  {/* Vehicle Type */}
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: "0.85rem" }}>
                      {row.booking.trip_info?.vehicle_type || "N/A"}
                    </Typography>
                  </TableCell>

                  {/* Customer */}
                  <TableCell sx={cellSx}>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        color: "primary.main",
                        lineHeight: 1.2,
                      }}
                    >
                      {row.booking.customer_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        lineHeight: 1.2,
                      }}
                    >
                      {row.booking.customer_mobile}
                    </Typography>
                  </TableCell>

                  {/* Trip Details – animated expand/collapse */}
                  <TableCell sx={cellSx}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.5,
                      }}
                    >
                      {/* The text container with smooth max-height transition */}
                      <Box
                        sx={{
                          maxHeight: isExpanded ? "500px" : "4em", // 3 lines collapsed, large value when expanded
                          overflow: "hidden",
                          transition: "max-height 0.4s ease",
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {tripDetails || "N/A"}
                        </Typography>
                      </Box>
                      {/* Toggle arrow (only if text is long) */}
                      {showToggle && (
                        <IconButton
                          size="small"
                          onClick={() => toggleTripExpand(bookingId)}
                          sx={{ p: 0, flexShrink: 0, mt: 0.3 }}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>

                  {/* Customer Feedback – compact multiline */}
                  <TableCell sx={cellSx}>
                    <TextField
                      multiline
                      minRows={1}
                      maxRows={3}
                      value={getEditedValue(
                        bookingId,
                        "customer_feedback",
                        row.booking.trip_info?.customer_feedback || "",
                      )}
                      onChange={(e) =>
                        handleFieldChange(
                          bookingId,
                          "customer_feedback",
                          e.target.value,
                        )
                      }
                      placeholder="Enter feedback..."
                      size="small"
                      fullWidth
                      inputProps={{
                        style: { fontSize: "0.85rem", padding: "2px 6px" },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& textarea": {
                            fontSize: "0.85rem",
                          },
                        },
                      }}
                    />
                  </TableCell>

                  {/* Action */}
                  <TableCell sx={cellSx}>
                    {hasChanges && (
                      <IconButton
                        onClick={() => handleSaveRow(bookingId)}
                        disabled={isSaving}
                        size="small"
                        color="primary"
                        sx={{
                          p: 0.4,
                          bgcolor: "primary.main",
                          color: "white",
                          "&:hover": { bgcolor: "primary.dark" },
                          "&.Mui-disabled": { bgcolor: "grey.300" },
                        }}
                      >
                        {isSaving ? (
                          <CircularProgress size={13} color="inherit" />
                        ) : (
                          <SaveIcon sx={{ fontSize: 14 }} />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredBookings.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 2.5 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "0.9rem" }}
                  >
                    No trips found for this date
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
